import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {{ next: number | null, previous: number | null }} */
  #ronPageBounds = { next: null, previous: null };
  #ronInFlight = new Map();
  #ronGeneration = 0;
  #ronAwaitingFilter = false;
  #ronFilterObserver;
  #ronQuery = '';

  get #isRonSearch() {
    return this.matches('results-list.ron-native-search');
  }

  #resetRonPagination() {
    this.#ronFilterObserver?.disconnect();
    this.#ronGeneration++;
    this.#ronPageBounds = { next: null, previous: null };
    this.#ronInFlight.clear();
    this.#ronAwaitingFilter = false;
    this.pages.clear();
    this.querySelectorAll('[data-ron-pagination-fallback]').forEach((link) => link.remove());
  }

  #ronQueryKey() {
    const params = new URL(window.location.href).searchParams;
    const criteria = [['q', params.get('q') || ''], ['sort_by', params.get('sort_by') || this.dataset.ronDefaultSort || 'relevance']];
    for (const [key, value] of params) {
      if (key === 'type' || key.startsWith('filter.') || key.startsWith('options[')) criteria.push([key, value]);
    }
    return JSON.stringify(criteria.sort((a, b) => a.join('=').localeCompare(b.join('='))));
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#isRonSearch) {
      this.#resetRonPagination();
      this.#ronQuery = this.#ronQueryKey();
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#isRonSearch) this.#resetRonPagination();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#isRonSearch && this.#ronAwaitingFilter) {
      this.#ronFilterObserver?.disconnect();
      this.#ronAwaitingFilter = false;
      this.#ronQuery = this.#ronQueryKey();
      this.#observeViewMore();
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const ronGeneration = this.#ronGeneration;
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    let pageContent;
    try {
      pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    } catch (error) {
      if (!this.#isRonSearch) throw error;
      return;
    }
    if (this.#isRonSearch && (ronGeneration !== this.#ronGeneration || !this.isConnected)) return;
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#isRonSearch) return this.#renderRonPage('next');
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    if (this.#isRonSearch) return this.#renderRonPage('previous');
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Search ranking can overlap server pages. Keep the first rendered product,
   * while advancing the server cursor even when a page adds no unique cards.
   * Generic native lists continue to use the original render methods above.
   * @param {"next" | "previous"} type
   */
  async #renderRonPage(type) {
    const sentinel = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
    if (this.#ronAwaitingFilter || this.#ronInFlight.has(type) || sentinel?.querySelector('[data-ron-pagination-fallback]')) return;
    const generation = this.#ronGeneration;
    this.#ronInFlight.set(type, generation);

    try {
      // Bound automatic recovery. If three entire pages overlap, offer a real
      // server-page link instead of leaving a visible sentinel unable to retrigger.
      for (let emptyPages = 0; emptyPages < 3; emptyPages++) {
        if (generation !== this.#ronGeneration || !this.isConnected) return;
        const { grid } = this.refs;
        const page = this.#getPage(type);
        if (!grid || !page || !this.#shouldUsePage(page)) return;

        let items = this.#getGridForPage(page.page);
        if (!items) {
          await this.#fetchSpecificPage(page.page, new URL(page.url));
          if (generation !== this.#ronGeneration || !this.isConnected || grid !== this.refs.grid) return;
          items = this.#getGridForPage(page.page);
          if (!items) {
            this.pages.delete(page.page);
            this.#showRonPageLink(type, page, sentinel);
            return;
          }
        }

        const seen = new Set(
          Array.from(grid.querySelectorAll(':scope > [data-product-id]'), (card) => card.dataset.productId)
        );
        const uniqueItems = Array.from(items).filter((card) => {
          const id = card.dataset.productId;
          if (!id) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        const firstElement = grid.firstElementChild;
        const scrollTop = window.scrollY;
        const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + scrollTop : 0;

        if (type === 'next') grid.append(...uniqueItems);
        else grid.prepend(...uniqueItems);
        // Retained cards carry the native page cursor. A separate bound is only
        // needed for a page that adds nothing, so a later no-op morph cannot
        // prune cards while leaving a cursor beyond the surviving DOM.
        this.#ronPageBounds[type] = uniqueItems.length ? null : page.page;
        this.#aspectRatioHelper?.processNewElements();
        history.pushState('', '', page.url.toString());

        if (type === 'previous' && firstElement && uniqueItems.length) {
          const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: scrollTop + newHeight - oldHeight, behavior: 'instant' });
        }

        if (uniqueItems.length) {
          requestIdleCallback(() => {
            if (generation === this.#ronGeneration && this.isConnected) this.#fetchPage(type);
          });
          return;
        }
      }

      const nextPage = this.#getPage(type);
      this.#showRonPageLink(type, nextPage, sentinel);
    } finally {
      if (this.#ronInFlight.get(type) === generation) this.#ronInFlight.delete(type);
    }
  }

  #showRonPageLink(type, page, sentinel) {
    if (!sentinel || !page || !this.#shouldUsePage(page) || sentinel.querySelector('[data-ron-pagination-fallback]')) return;
    const link = document.createElement('a');
    link.href = page.url.toString();
    link.rel = type === 'next' ? 'next' : 'prev';
    link.className = 'button button-secondary';
    link.dataset.ronPaginationFallback = '';
    link.textContent = type === 'next' ? this.dataset.ronNextLabel || 'Next' : this.dataset.ronPreviousLabel || 'Previous';
    sentinel.append(link);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    let currentCardPage = Number(targetCard.dataset.page);
    const consumedPage = this.#isRonSearch ? this.#ronPageBounds[type] : null;
    if (consumedPage !== null) {
      currentCardPage = isPrevious ? Math.min(currentCardPage, consumedPage) : Math.max(currentCardPage, consumedPage);
    }
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    const sameRonFirstPage = this.#isRonSearch && this.#ronQuery === this.#ronQueryKey() &&
      (this.refs.cards || []).every((card) => Number(card.dataset.page) === 1);
    if (this.#isRonSearch) this.#resetRonPagination();
    this.pages.clear();

    if (this.#isRonSearch) {
      this.#ronAwaitingFilter = true;
      this.infinityScrollObserver?.disconnect();
      if (sameRonFirstPage) {
        this.updatedCallback();
        return;
      }
      const generation = this.#ronGeneration;
      const originalGrid = this.refs.grid;
      this.#ronFilterObserver = new MutationObserver((mutations) => {
        if (generation !== this.#ronGeneration || !this.isConnected) return;
        const gridChanged = this.querySelector('[ref="grid"]') !== originalGrid || mutations.some((mutation) => {
          if (!originalGrid) return false;
          if (mutation.target === originalGrid) return true;
          return mutation.type === 'attributes' && mutation.target.parentElement === originalGrid &&
            ['data-page', 'data-product-id'].includes(mutation.attributeName);
        });
        const contextChanged = mutations.some((mutation) => mutation.target === this && mutation.attributeName === 'data-ron-search-context');
        if (gridChanged || contextChanged) this.updatedCallback();
      });
      // Observe the live grid: this theme's morph callback receives its inert
      // source node. Mutation delivery runs after the synchronous DOM update.
      this.#ronFilterObserver.observe(this, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['data-ron-search-context', 'data-last-page', 'data-page', 'data-product-id'],
      });
      return;
    }

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
